import { CORE_URL } from "@/constants/api";
import { VITE_ALFRED_SERVICE_URL, VITE_CORE_API_URL } from "@/constants/env";
import { DashboardAnalytics } from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { auth, db } from "@/services/auth-service";
import { DB_FILTER, DB_PATH } from "@constants/db";
import {
	type DocumentData,
	type DocumentReference,
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	orderBy,
	query,
	updateDoc,
	where,
} from "firebase/firestore";
import type { SetStateAction } from "react";

export const getDocFromRef = async (ref: DocumentReference<unknown>) => {
	const docSnap = await getDoc(ref);
	return docSnap;
};

export const getDocFromPath = async (path: string) => {
	const docSnap = await getDoc(doc(db, path));
	return docSnap;
};

export const getOrganizations = async () => {
	try {
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/all`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch all organizations: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfAllowedOrganizations = async () => {
	try {
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/self/allowed_organizations`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch self allowed organizations: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfOrganization = async () => {
	try {
		console.log("Fetching user self organization");
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/self`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch self organization: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfOrganizationForms = async () => {
	try {
		console.log("Fetching user self organization forms");
		const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/self/forms`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch self organization forms: ${errorDetails.detail || errorDetails.message}`,
				);
			});
		}
		const result = await response.json();
		// Transform Core API response format if needed
		if (result.success && result.data) {
			return result.data;
		}
		return result;
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfOrganizationReports = async () => {
	try {
		console.log("Fetching user self organization reports");
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/self/reports`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch self organization reports: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfOrganizationGraphs = async () => {
	try {
		console.log("Fetching user self organization graphs");
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/self/graphs`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch self organization graphs: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getOrgSpecificInputs = async (
	organization: {
		external_firebase_id: string;
		config: { org_specific_inputs: any };
	},
	setState: SetStateAction<any>,
) => {
	console.log(organization, "organizationPassed");
	if (organization.external_firebase_id !== "SSPAMERICA") {
		console.log(organization.config, "org_specific_inputs");
		if (organization.config.org_specific_inputs) {
			setState(organization.config.org_specific_inputs);
		}
	}
};

export const getOrgAdditionalLocationInputs = async (
	organization: {
		config: { org_specific_inputs: undefined };
		external_firebase_id: string;
	},
	setState: (arg0: any) => void,
) => {
	// console.log(organization, 'organization');
	if (
		organization.config?.org_specific_inputs !== undefined &&
		organization.external_firebase_id === "SSPAMERICA"
	) {
		setState(organization.config?.org_specific_inputs);
	}
};

export const getManufacturer = async (name: any) => {
	try {
		const url = `${VITE_ALFRED_SERVICE_URL}/manufacturer/lookup/${name}`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch single manufacturer: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getManufacturers = async () => {
	try {
		const url = `${VITE_ALFRED_SERVICE_URL}/manufacturer/all`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch all manufacturers: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

export const getSelfOrganizationManufacturers = async () => {
	try {
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/manufacturer/all`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch all manufacturers: ${errorDetails.detail}`,
				);
			});
		}
		return await response.json();
	} catch (exception: any) {
		throw new Error(exception);
	}
};

// Gets list of manufacturers for form and sets it to the appropriate state
export const getManufacturersForForm = async (setState: any) => {
	const manufacturers = await getManufacturers();
	//Converting to a dictionary
	const parsed = manufacturers.reduce(
		(acc: { [x: string]: any }, item: { id: string | number }) => {
			acc[item.id] = item;
			return acc;
		},
		{},
	);
	setState(parsed);
};

export const getReleases = async () => {
	const snap = await getDocs(
		query(collection(db, DB_PATH.RELEASES), orderBy("createdAt", "desc")),
	);
	return snap;
};

export const selfUpdateOrganization = async (data: any) => {
	const url = `${
		import.meta.env.VITE_ALFRED_SERVICE_URL
	}/organization/self/update_config`;

	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify(data),
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to update user organization config: ${errorDetails.detail}`,
			);
		});
	}

	return await response.json();
};

export const addManufacturer = async (name: any) => {
	await addDoc(collection(db, DB_PATH.MANUFACTURERS), {
		name: name,
	});
};

export const deleteWithRef = async (ref: DocumentReference<unknown>) => {
	await deleteDoc(ref);
};

export const updateWithRef = async (
	ref: DocumentReference<any>,
	data: { assetRef: DocumentReference<DocumentData> },
) => {
	await updateDoc(ref, data);
};

export const updateManufacterFromQA = async (data: {
	manufacturer: any;
	ifOtherManufacturer?: string;
}) => {
	data.manufacturer = data?.ifOtherManufacturer?.toUpperCase();
	if (data.ifOtherManufacturer) {
		data.ifOtherManufacturer = undefined;
	}
	const manufacturersSnap = await getManufacturer(data.manufacturer);
	if (!manufacturersSnap) {
		await addManufacturer(data.manufacturer);
	}
};

export const getOrgInputsAndAutoSave = async (
	organization: {
		config: { auto_save_drafts: any; org_specific_inputs: any };
		external_firebase_id: string;
	},
	isEditing: any,
	setAutoSave: (arg0: boolean) => void,
	setInputs: (arg0: any) => void,
) => {
	if (isEditing) {
		setAutoSave(false);
	} else {
		setAutoSave(organization.config.auto_save_drafts);
	}

	if (
		organization.config.org_specific_inputs &&
		organization.external_firebase_id !== "SSPAMERICA"
	) {
		setInputs(organization.config.org_specific_inputs);
	}
};

export const populateEquipmentAreas = async (setState: (arg0: any) => void) => {
	const areas = await selfOrganizationEquipmentAreas();
	setState(areas);
};

export const smartManufacturerOptions = async (assetTypeId: any) => {
	const url = `${
		import.meta.env.VITE_ALFRED_SERVICE_URL
	}/manufacturer/smart/${assetTypeId}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch smart manufacturers for ${assetTypeId}: ${errorDetails.detail}`,
			);
		});
	}

	const manufacturers = await response.json();

	if (manufacturers && manufacturers.length === 0) {
		console.error("No manufacturers found for assetType:", assetTypeId);
	}

	return manufacturers;
};

export const getManufacturerDoc = async (manufacturer: unknown) => {
	if (manufacturer) {
		const colRef = collection(db, DB_PATH.MANUFACTURERS);
		const q = query(colRef, where(DB_FILTER.NAME, "==", manufacturer));
		const querySnapshot = await getDocs(q);
		return querySnapshot.docs[0];
	}
};

export const selfOrganizationEquipmentAreas = async () => {
	const url = `${
		import.meta.env.VITE_CORE_API_URL
	}/core/organization/equipment-areas`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch equipment areas: ${errorDetails.message || errorDetails.detail || "Unknown error"}`,
			);
		});
	}

	const result = await response.json();

	// Core API returns { success: true, data: [{ id, name, createdAt, isDeprecated, value, label }, ...] }
	if (result.success && Array.isArray(result.data)) {
		return result.data;
	}

	// Fallback: return as-is if unexpected format
	return result.data || [];
};

export const createEquipmentArea = async (name: string) => {
	const url = `${import.meta.env.VITE_CORE_API_URL}/core/organization/equipment-areas`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ name }),
	});

	if (response.status !== 201) {
		const errorDetails = await response.json();
		throw new Error(
			errorDetails.message ||
				errorDetails.detail ||
				"Failed to create equipment area",
		);
	}

	const result = await response.json();
	return result.data;
};

export const updateEquipmentArea = async (
	areaId: number,
	updates: { name?: string; isDeprecated?: boolean },
) => {
	const url = `${import.meta.env.VITE_CORE_API_URL}/core/organization/equipment-areas/${areaId}`;

	const response = await fetch(url, {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(updates),
	});

	if (response.status !== 200) {
		const errorDetails = await response.json();
		throw new Error(
			errorDetails.message ||
				errorDetails.detail ||
				"Failed to update equipment area",
		);
	}

	const result = await response.json();
	return result.data;
};

export const deleteEquipmentArea = async (areaId: number) => {
	const url = `${import.meta.env.VITE_CORE_API_URL}/core/organization/equipment-areas/${areaId}`;

	const response = await fetch(url, {
		method: "DELETE",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		const errorDetails = await response.json();
		throw new Error(
			errorDetails.message ||
				errorDetails.detail ||
				"Failed to delete equipment area",
		);
	}

	const result = await response.json();
	return result.data;
};

export const selfOrganizationAssetTypes = async (includeDeleted = false) => {
	const url = `${
		import.meta.env.VITE_CORE_API_URL
	}/core/utility/asset-types${includeDeleted ? "?includeDeprecated=true" : ""}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch asset types: ${errorDetails.message || errorDetails.detail || "Unknown error"}`,
			);
		});
	}

	const result = await response.json();

	// Extract asset types from the response
	const assetTypes = result.data?.assetTypes || [];

	// Return array of asset type names (strings) for filter options
	// This matches the expected format: FilterOptions.assetTypes: string[]
	return assetTypes.map((assetType: any) => assetType.name);
};

export const selfOrganizationAssetTypeCategories = async () => {
	const url = `${
		import.meta.env.VITE_ALFRED_SERVICE_URL
	}/organization/self/asset_type_categories`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch asset type categories: ${errorDetails.detail}`,
			);
		});
	}

	return await response.json();
};

export const getAssetRanks = async (organization: string, formId: string) => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			formId,
			DB_PATH.ASSET_RANKS,
		);

		const querySnapshot = await getDocs(colRef);

		const ranks: any[] = [];
		querySnapshot.forEach((doc) => {
			const data = doc.data();
			if (data?.name) {
				ranks.push(data.name);
			}
		});

		return ranks;
	} catch (error) {
		console.error("Error fetching asset ranks:", error);
		throw error;
	}
};

export const getAssetClasses = async (organization: string, formId: string) => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			formId,
			DB_PATH.ASSET_CLASSES,
		);

		const querySnapshot = await getDocs(colRef);

		const classes: any[] = [];
		querySnapshot.forEach((doc) => {
			const data = doc.data();
			if (data?.name) {
				classes.push(data.name);
			}
		});

		return classes;
	} catch (error) {
		console.error("Error fetching asset classes:", error);
		throw error;
	}
};

export const getAssetNumbers = async (organization: string, formId: string) => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			formId,
			DB_PATH.ASSET_NUMBERS,
		);

		const querySnapshot = await getDocs(colRef);

		const numbers: any[] = [];
		querySnapshot.forEach((doc) => {
			const data = doc.data();
			if (data?.name) {
				numbers.push(data.name);
			}
		});

		return numbers;
	} catch (error) {
		console.error("Error fetching asset numbers:", error);
		throw error;
	}
};

export const getPropertyIds = async (organization: string, formId: string) => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			formId,
			DB_PATH.PROPERTY_IDS,
		);

		const querySnapshot = await getDocs(colRef);

		const propertyIds: any[] = [];
		querySnapshot.forEach((doc) => {
			const data = doc.data();
			if (data?.name) {
				propertyIds.push(data.name);
			}
		});

		return propertyIds;
	} catch (error) {
		console.error("Error fetching property ids:", error);
		throw error;
	}
};

export const getAssetLocations = async (
	organization: string,
	formId: string,
) => {
	try {
		const colRef = collection(
			db,
			DB_PATH.ORGANIZATIONS,
			organization,
			DB_PATH.FORMS,
			formId,
			DB_PATH.EQUIPMENT_AREAS,
		);

		const querySnapshot = await getDocs(colRef);

		const locations: any[] = [];
		querySnapshot.forEach((doc) => {
			const data = doc.data();
			if (data?.name) {
				locations.push(data.name);
			}
		});

		return locations;
	} catch (error) {
		console.error("Error fetching asset locations:", error);
		throw error;
	}
};

export const getSelfOrganizationLocations = async () => {
	const url = `${VITE_ALFRED_SERVICE_URL}/location/self/all`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch self organization locations: ${errorDetails.detail}`,
			);
		});
	}

	return await response.json();
};

export const getOrganizationReport = async ({
	reportId,
	locationId,
}: { reportId: string; locationId?: string }): Promise<Blob> => {
	if (!reportId) {
		throw new Error("Report ID is required");
	}

	// Use new Core API endpoint for reports
	const url = new URL(
		`${VITE_CORE_API_URL}/core/organization/reports/${reportId}`,
	);

	if (locationId !== undefined) {
		url.searchParams.append("location_id", locationId.toString());
	}

	try {
		const response = await fetch(url.toString(), {
			method: "GET",
			headers: {
				Accept: "application/pdf",
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(
				errorData.detail ||
					errorData.details ||
					"Failed to fetch organization report",
			);
		}

		// Return PDF as blob
		const blob = await response.blob();

		// Trigger download
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = `leave-behind-report-${locationId || "all"}-${Date.now()}.pdf`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);

		return blob;
	} catch (error) {
		console.error("Report generation error:", error);
		throw new Error(
			error instanceof Error
				? error.message
				: "Failed to fetch organization report",
		);
	}
};

export interface ChecklistsZipParams {
	formId?: number | string;
	locationId?: number | string;
	brand?: string;
	startDate?: string;
	endDate?: string;
	isApproved?: boolean;
}

export interface ChecklistsZipJob {
	jobId: string;
	total: number;
	filename: string;
}

export interface ChecklistsZipJobStatus {
	jobId: string;
	status: "running" | "done" | "error";
	total: number;
	completed: number;
	filename: string;
	error?: string;
}

export interface ChecklistBrandOption {
	brand: string;
	count: number;
}

const CHECKLISTS_ZIP_URL = `${VITE_CORE_API_URL}/core/organization/reports/checklists-zip`;

/** List brands (with checklist counts) available for the export dropdown. */
export const getChecklistsZipBrands = async (
	formId?: number | string,
): Promise<ChecklistBrandOption[]> => {
	const url = new URL(`${CHECKLISTS_ZIP_URL}/brands`);
	if (formId !== undefined) url.searchParams.append("form_id", String(formId));

	const response = await fetch(url.toString(), {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});
	if (!response.ok) {
		throw new Error(await readErrorMessage(response, "Failed to load brands"));
	}
	const data = await response.json();
	return data.brands ?? [];
};

async function readErrorMessage(
	response: Response,
	fallback: string,
): Promise<string> {
	try {
		const data = await response.json();
		return data.error || data.details || fallback;
	} catch {
		return fallback;
	}
}

/**
 * Start an async checklist ZIP export job (Vixxo-only). Returns the job id and
 * the number of checklists that will be included.
 */
export const startChecklistsZipJob = async (
	params: ChecklistsZipParams = {},
): Promise<ChecklistsZipJob> => {
	const url = new URL(CHECKLISTS_ZIP_URL);
	if (params.formId !== undefined)
		url.searchParams.append("form_id", String(params.formId));
	if (params.locationId !== undefined && params.locationId !== "")
		url.searchParams.append("location_id", String(params.locationId));
	if (params.brand) url.searchParams.append("brand", params.brand);
	if (params.startDate) url.searchParams.append("start_date", params.startDate);
	if (params.endDate) url.searchParams.append("end_date", params.endDate);
	if (typeof params.isApproved === "boolean")
		url.searchParams.append("is_approved", String(params.isApproved));

	const response = await fetch(url.toString(), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		throw new Error(
			await readErrorMessage(response, "Failed to start checklist export"),
		);
	}
	return response.json();
};

/** Poll the status/progress of a checklist ZIP export job. */
export const getChecklistsZipJobStatus = async (
	jobId: string,
): Promise<ChecklistsZipJobStatus> => {
	const response = await fetch(`${CHECKLISTS_ZIP_URL}/${jobId}/status`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});
	if (!response.ok) {
		throw new Error(
			await readErrorMessage(response, "Failed to check export status"),
		);
	}
	return response.json();
};

/** Download the finished ZIP for a completed job and trigger a browser save. */
export const downloadChecklistsZipResult = async (
	jobId: string,
	filename: string,
): Promise<void> => {
	const response = await fetch(`${CHECKLISTS_ZIP_URL}/${jobId}/download`, {
		method: "GET",
		headers: {
			Accept: "application/zip",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});
	if (!response.ok) {
		throw new Error(
			await readErrorMessage(response, "Failed to download checklist export"),
		);
	}

	const blob = await response.blob();
	const downloadUrl = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = downloadUrl;
	link.download = filename || `checklists-${Date.now()}.zip`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(downloadUrl);
};

export const getOrganizationDashboardAnalytics =
	async (): Promise<DashboardAnalytics> => {
		const url = `${VITE_ALFRED_SERVICE_URL}/organization/self/dashboard-analytics`;

		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
			},
		});

		if (response.status !== 200) {
			return response.json().then((errorDetails) => {
				throw new Error(
					`Failed to fetch organization dashboard analytics: ${errorDetails.detail}`,
				);
			});
		}

		return await response.json();
	};

export const getOrganizationAssetTypes = async (
	includeDeprecated: boolean = false,
) => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/asset-types${includeDeprecated ? "?includeDeprecated=true" : ""}`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(`Failed to fetch asset types: ${errorDetails.detail}`);
		});
	}

	return await response.json();
};

export const addOrganizationFormUniqueField = async (
	formId: number,
	field: any,
) => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/self/form/${formId}/unique-fields`;

	const response = await fetch(url, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify({ field }),
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to add organization form unique field: ${errorDetails.detail || errorDetails.message}`,
			);
		});
	}

	const result = await response.json();
	return result.data?.form || result.data || result;
};

export const updateOrganizationFormUniqueFields = async (
	formId: number,
	uniqueFields: any[] | null,
) => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/self/form/${formId}/unique-fields`;

	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify({ uniqueFields }),
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to update organization form unique fields: ${errorDetails.detail || errorDetails.message}`,
			);
		});
	}

	return await response.json();
};

export const getOrganizationFormUniqueFields = async (formId: number) => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/self/form/${formId}/unique-fields`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to fetch organization form unique fields: ${errorDetails.detail || errorDetails.message}`,
			);
		});
	}

	const result = await response.json();
	return result.data?.uniqueFields || null;
};

export const updateOrganizationConfig = async (data: any) => {
	const url = `${VITE_CORE_API_URL}${CORE_URL}/utility/config`;

	const response = await fetch(url, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify(data),
	});

	if (response.status !== 200) {
		return response.json().then((errorDetails) => {
			throw new Error(
				`Failed to update organization config: ${errorDetails.detail}`,
			);
		});
	}

	return await response.json();
};
